// @ts-check

/**
 * @file Renders user settings for locale and theme preferences.
 */

import {FlashNotifications} from "flash-notifications"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import gettextConfig from "gettext-universal/build/src/config.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native"
import Card from "../src/components/card"
import EmptyState from "../src/components/empty-state"
import errorMessage from "../src/shared/error-message"
import LoadingState from "../src/components/loading-state"
import PageHeader from "../src/components/page-header"
import {User} from "../src/frontend-models/user"
import {detectSystemTheme} from "../src/shared/theme"
import {updateCurrentUser, userFromProfileCommandResponse} from "../src/auth/use-current-user"
import useThemeColors, {useThemeName} from "../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} SettingsScreenProps */

/**
 * @typedef {object} SettingsScreenState
 * @property {unknown} currentLocale Active locale.
 * @property {{id: string | number, locale: string | null, theme: string | null} | null} currentUser Loaded settings user.
 * @property {boolean} loading Whether profile settings are loading.
 * @property {"dark" | "light"} themeName Selected theme name.
 */

/**
 * Settings screen — locale + theme controls backed by User.changeLocale / changeTheme.
 * @augments {ShapeComponent<SettingsScreenProps, SettingsScreenState>}
 */
class SettingsScreen extends ShapeComponent {
  /** @type {SettingsScreenState} */
  state = {
    currentLocale: gettextConfig.getLocale() || "en",
    currentUser:
      /** @type {{id: string | number, locale: string | null, theme: string | null} | null} */ null,
    loading: true,
    themeName: /** @type {"dark" | "light"} */ detectSystemTheme()
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  paletteThemeName

  /** Initializes translations, palette, and settings loading. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.paletteThemeName = useThemeName()
    useEffect(() => {
      void this.loadProfile()
    }, [])
  }

  /**
   * Loads the signed-in user's settings profile.
   * @returns {Promise<void>} Resolves when profile loading completes.
   */
  loadProfile = async () => {
    try {
      const response = await User.currentProfile()
      const user = userFromProfileCommandResponse(response)
      if (user) {
        this.s.currentLocale =
          user.locale || gettextConfig.getLocale() || "en"
        this.s.currentUser = {
          id: user.id,
          locale: user.locale || null,
          theme: user.theme || null
        }
        this.s.loading = false
        this.s.themeName =
          user.theme === "dark" || user.theme === "light"
            ? user.theme
            : detectSystemTheme()
      } else {
        this.s.loading = false
      }
    } catch (error) {
      FlashNotifications.errorResponse(error)
      this.s.loading = false
    }
  }

  /**
   * Saves a selected locale.
   * @param {string} locale Locale to save.
   * @returns {Promise<void>} Resolves when locale handling completes.
   */
  onLocalePress = async (locale) => {
    if (locale === this.s.currentLocale) return
    this.s.currentLocale = locale
    try {
      await User.changeLocale({
        locale
      })
      // Mirror onThemePress: patch the global current-user context so
      // every consumer reacts to the change. updateCurrentUser invokes
      // syncLocaleFromCurrentUser, which sets the active gettext locale
      // and persists it to the local Option store so a window refresh
      // starts from this value instead of re-deriving from the browser.
      updateCurrentUser({locale})
      FlashNotifications.success(_("Settings saved"))
    } catch (error) {
      FlashNotifications.error(errorMessage(error))
    }
  }

  /**
   * Saves a selected theme.
   * @param {"dark" | "light"} themeName Theme name to save.
   * @returns {Promise<void>} Resolves when theme handling completes.
   */
  onThemePress = async (themeName) => {
    if (themeName === this.s.themeName) return
    this.s.themeName = themeName
    try {
      await User.changeTheme({
        theme: themeName
      })
      updateCurrentUser({theme: themeName})
      FlashNotifications.success(_("Settings saved"))
    } catch (error) {
      FlashNotifications.error(errorMessage(error))
    }
  }

  /**
   * Renders settings controls.
   * @returns {ReactNode} Settings screen content.
   */
  render() {
    const {currentLocale, currentUser, loading, themeName} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="settingsScreen"
      >
        <ScreenTitle>{_("Settings")}</ScreenTitle>
        <View
          testID="settingsScreen/content"
        >
          <PageHeader
            subtitle={_("Personalize the app to your preferences")}
            testID="settingsScreen/header"
            title={_("Settings")}
          />
          {loading && <LoadingState testID="settingsScreen/loading" />}
          {!loading && !currentUser &&
            <EmptyState
              message={_("Sign in to manage your settings")}
              testID="settingsScreen/empty"
            />
          }
          {!loading && currentUser &&
            <View
              style={styles.cards ||= {
                gap: 16
              }}
              testID="settingsScreen/cards"
            >
              <Card
                style={styles.cardSpacing ||= {gap: 12}}
                testID="settingsScreen/themeCard"
              >
                <Text
                  style={styles[`cardTitle-${this.tt.paletteThemeName}`] ||= {
                    fontSize: 16,
                    fontWeight: 700
                  }}
                  testID="settingsScreen/themeCard/title"
                >
                  {_("Appearance")}
                </Text>
                <View
                  style={styles.row ||= {
                    flexDirection: "row",
                    gap: 8
                  }}
                  testID="settingsScreen/themeCard/row"
                >
                  {[
                    {
                      label: _("Light theme"),
                      value: "light"
                    },
                    {
                      label: _("Dark theme"),
                      value: "dark"
                    }
                  ].map((option) => {
                    const active = option.value === themeName
                    const themeValue =
                      /** @type {"dark" | "light"} */ (option.value)
                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => this.tt.onThemePress(themeValue)}
                        style={styles[`themeChip-${this.tt.paletteThemeName}-${option.value}-${active}`] ||= {
                          alignItems: "center",
                          backgroundColor: active ? this.tt.colors.primary : this.tt.colors.surfaceMuted,
                          borderRadius: 999,
                          flexDirection: "row",
                          paddingHorizontal: 14,
                          paddingVertical: 8
                        }}
                        testID={`settingsScreen/themeCard/chip/${option.value}`}
                      >
                        <View
                          style={styles.themeChipIcon ||= {
                            marginRight: 8,
                            width: 14
                          }}
                          testID={`settingsScreen/themeCard/chip/${option.value}/icon`}
                        >
                          <FontAwesome6
                            color={active ? this.tt.colors.onPrimary : this.tt.colors.textMuted}
                            name={option.value === "dark" ? "moon" : "sun"}
                            size={13}
                          />
                        </View>
                        <Text
                          style={styles[ `themeChipText-${this.tt.paletteThemeName}-${option.value}-${active}` ] ||= {
                            color: active ? this.tt.colors.onPrimary : this.tt.colors.textMuted,
                            fontSize: 13,
                            fontWeight: 700
                          }}
                          testID={`settingsScreen/themeCard/chip/${option.value}/label`}
                        >
                          {option.label}
                        </Text>
                      </Pressable>
                    )
                  })}
                </View>
              </Card>
              <Card
                style={styles.cardSpacing ||= {gap: 12}}
                testID="settingsScreen/localeCard"
              >
                <Text
                  style={styles[`localeCardTitle-${this.tt.paletteThemeName}`] ||= {
                    fontSize: 16,
                    fontWeight: 700
                  }}
                  testID="settingsScreen/localeCard/title"
                >
                  {_("Language")}
                </Text>
                <View
                  style={styles.localeRow ||= {
                    flexDirection: "row",
                    gap: 8
                  }}
                  testID="settingsScreen/localeCard/row"
                >
                  {[
                    {
                      label: "English",
                      value: "en"
                    },
                    {
                      label: "Dansk",
                      value: "da"
                    },
                    {
                      label: "Deutsch",
                      value: "de"
                    }
                  ].map((option) => {
                    const active = option.value === currentLocale
                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => this.tt.onLocalePress(option.value)}
                        style={styles[`localeChip-${this.tt.paletteThemeName}-${option.value}-${active}`] ||= {
                          alignItems: "center",
                          backgroundColor: active ? this.tt.colors.primary : this.tt.colors.surfaceMuted,
                          borderRadius: 999,
                          flexDirection: "row",
                          paddingHorizontal: 14,
                          paddingVertical: 8
                        }}
                        testID={`settingsScreen/localeCard/chip/${option.value}`}
                      >
                        <View
                          style={styles.localeChipIcon ||= {
                            marginRight: 8,
                            width: 14
                          }}
                          testID={`settingsScreen/localeCard/chip/${option.value}/icon`}
                        >
                          <FontAwesome6
                            color={active ? this.tt.colors.onPrimary : this.tt.colors.textMuted}
                            name="language"
                            size={13}
                          />
                        </View>
                        <Text
                          style={styles[ `localeChipText-${this.tt.paletteThemeName}-${option.value}-${active}` ] ||= {
                            color: active ? this.tt.colors.onPrimary : this.tt.colors.textMuted,
                            fontSize: 13,
                            fontWeight: 700
                          }}
                          testID={`settingsScreen/localeCard/chip/${option.value}/label`}
                        >
                          {option.label}
                        </Text>
                      </Pressable>
                    )
                  })}
                </View>
              </Card>
            </View>
          }
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<SettingsScreenProps>} */
const SettingsScreenComponent = memo(shapeComponent(SettingsScreen))

export default SettingsScreenComponent
